import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { TrendingDown } from "lucide-react";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/95 px-3 py-2 mono text-[11px]">
      <div className="text-slate-400">step {p.t}</div>
      <div className={p.fault ? "text-red-400" : "text-cyan-300"}>
        V(x) = {p.vx.toFixed(4)}
      </div>
    </div>
  );
};

export const LyapunovMeter = ({ vxSeries, threshold, quarantine }) => {
  const last = vxSeries[vxSeries.length - 1]?.vx ?? 0;
  const converging = last < threshold;

  return (
    <div className="glass-panel rounded-2xl p-5 h-full flex flex-col" data-testid="lyapunov-chart-container">
      <div className="flex items-center justify-between mb-1">
        <h3 className="mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
          Lyapunov Stability · V(x)
        </h3>
        <span
          className={`mono text-[10px] font-semibold px-2 py-0.5 rounded ${
            converging
              ? "text-emerald-300 bg-emerald-950/50"
              : "text-red-300 bg-red-950/50"
          }`}
        >
          {converging ? "V̇(x) < 0 · DESCENDING" : "V̇(x) > 0 · VIOLATION"}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <TrendingDown className="h-4 w-4 text-cyan-400" />
        <span
          className="mono text-2xl font-extrabold tabular-nums"
          style={{ color: converging ? "#22d3ee" : "#ef4444" }}
        >
          {last.toFixed(4)}
        </span>
        <span className="mono text-[10px] text-slate-500">
          / threshold {threshold.toFixed(2)}
        </span>
      </div>
      <div className="flex-1 min-h-[180px]">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
          <LineChart data={vxSeries} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="t" tick={{ fill: "#475569", fontSize: 9 }} axisLine={{ stroke: "#1e293b" }} tickLine={false} />
            <YAxis domain={[0, 2]} tick={{ fill: "#475569", fontSize: 9 }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={threshold}
              stroke="#ef4444"
              strokeDasharray="5 4"
              strokeOpacity={0.7}
              label={{ value: "V_max", fill: "#ef4444", fontSize: 9, position: "insideTopRight" }}
            />
            <Line
              type="monotone"
              dataKey="vx"
              stroke={quarantine ? "#ef4444" : "#06b6d4"}
              strokeWidth={2}
              dot={(props) =>
                props.payload.fault ? (
                  <circle key={props.key} cx={props.cx} cy={props.cy} r={4} fill="#ef4444" stroke="#fff" strokeWidth={1} />
                ) : null
              }
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
